import { ref, onMounted, onUnmounted, type Ref, watch } from 'vue';

interface UseHeaderScroll {
  isSticky: Ref<boolean>;
  isTop: Ref<boolean>;
  isBottom: Ref<boolean>;
  headerHeight: Ref<number>;
}

export const useHeaderScroll = (
  scrollThreshold: number = 100
): UseHeaderScroll => {
  const isSticky = ref(false);
  const isTop = ref(true);
  const isBottom = ref(false);
  const lastPosition = ref<number>(0);
  const headerHeight = ref<number>(0);

  let ticking = false;
  let innerEl: HTMLElement | null = null;
  let resizeObserver: ResizeObserver | null = null;

  const updateHeaderHeight = () => {
    if (innerEl) {
      headerHeight.value = innerEl.offsetHeight;
    }
  };

  const updateScrollState = () => {
    const currentScroll = window.scrollY;

    if (currentScroll > lastPosition.value) {
      isSticky.value = true;
    } else if (currentScroll <= scrollThreshold) {
      isSticky.value = true;
    } else {
      isSticky.value = false;
    }

    isTop.value = currentScroll < scrollThreshold;
    lastPosition.value = currentScroll;

    const docHeight = document.documentElement.scrollHeight;
    isBottom.value =
      currentScroll + window.innerHeight >= docHeight - scrollThreshold;

    ticking = false;
  };

  const handleScroll = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollState);
      ticking = true;
    }
  };

  onMounted(() => {
    innerEl = document.querySelector<HTMLElement>('.headroom--inner > .container');
    updateHeaderHeight();

    window.addEventListener('scroll', handleScroll, { passive: true });

    if (innerEl) {
      resizeObserver = new ResizeObserver(() => updateHeaderHeight());
      resizeObserver.observe(innerEl);
    }

    updateScrollState();
  });

  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    if (resizeObserver && innerEl) {
      resizeObserver.unobserve(innerEl);
    }
  });

  watch(headerHeight, (val) => {
    document.documentElement.style.setProperty('--height-navbar', `${val}px`);
  });

  return { isSticky, isTop, isBottom, headerHeight };
};
